import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { informationTypesAPI } from '../services/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Texte' },
  { value: 'number', label: 'Nombre' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Texte long' },
];

const InformationTypesPage = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text',
    max_length: 255,
    is_required: false,
    is_active: true,
    description: '',
    display_order: 0,
    custom_position_index: '',
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await informationTypesAPI.getAll();
      setTypes(response.data?.results || response.data || []);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        field_type: type.field_type,
        max_length: type.max_length,
        is_required: type.is_required,
        is_active: type.is_active,
        description: type.description || '',
        display_order: type.display_order || 0,
        custom_position_index: type.custom_position_index || '',
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        field_type: 'text',
        max_length: 255,
        is_required: false,
        is_active: true,
        description: '',
        display_order: 0,
        custom_position_index: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingType(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }

    try {
      setSubmitting(true);
      if (editingType) {
        await informationTypesAPI.update(editingType.id, formData);
        setSuccess('Type modifié avec succès');
      } else {
        await informationTypesAPI.create(formData);
        setSuccess('Type créé avec succès');
      }
      handleCloseDialog();
      loadTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce type ?')) {
      return;
    }

    try {
      await informationTypesAPI.delete(id);
      setSuccess('Type supprimé avec succès');
      loadTypes();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Types d'Informations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérer les types d'informations que les clients peuvent remplir
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau Type
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Ordre</strong></TableCell>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Type de champ</strong></TableCell>
              <TableCell><strong>Obligatoire</strong></TableCell>
              <TableCell><strong>Position</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucun type d'information</Typography>
                </TableCell>
              </TableRow>
            ) : (
              types.map((type) => (
                <TableRow key={type.id} hover>
                  <TableCell>{type.display_order}</TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>{type.name}</Typography>
                    {type.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {type.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={FIELD_TYPES.find(f => f.value === type.field_type)?.label || type.field_type} 
                      size="small" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={type.is_required ? 'Oui' : 'Non'} 
                      size="small" 
                      color={type.is_required ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {type.custom_position_index !== null ? type.custom_position_index : 'Non défini'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={type.is_active ? 'Actif' : 'Inactif'} 
                      size="small" 
                      color={type.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => handleOpenDialog(type)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(type.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType ? 'Modifier le type' : 'Nouveau type d\'information'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nom *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              select
              label="Type de champ"
              value={formData.field_type}
              onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
              fullWidth
            >
              {FIELD_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Longueur maximale"
              type="number"
              value={formData.max_length}
              onChange={(e) => setFormData({ ...formData, max_length: parseInt(e.target.value) || 255 })}
              fullWidth
            />
            <TextField
              label="Ordre d'affichage"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              fullWidth
              helperText="Les types sont affichés par ordre croissant"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Aide affichée sous le champ pour le client"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                />
              }
              label="Champ obligatoire"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Actif"
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Paramètres des documents
            </Typography>
            <TextField
              label="Position dans les documents"
              type="number"
              value={formData.custom_position_index}
              onChange={(e) => setFormData({ ...formData, custom_position_index: e.target.value ? parseInt(e.target.value) : '' })}
              fullWidth
              helperText="0 = après client, 1 = après raison sociale, 2 = après adresse, 3 = après RC/NIF/AI, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Enregistrement...' : (editingType ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InformationTypesPage;
